import React, { useState } from 'react';
import { Target, Briefcase, Scale, Laptop, Search, Users, ChevronDown } from 'lucide-react';
import { services } from '../data/mockData';
import { Card, CardContent } from './ui/card';

const iconMap = {
  Target: Target,
  Briefcase: Briefcase,
  Scale: Scale,
  Laptop: Laptop,
  Search: Search,
  Users: Users
};

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-amber-400/10 border border-amber-400/20 rounded-full px-4 py-2 mb-4">
            <span className="text-amber-600 font-medium text-sm">Our Services</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Comprehensive Consultancy Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Delivering innovative solutions across strategy, technology, legal advisory, and capacity building
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            const isExpanded = expandedService === service.id;

            return (
              <Card
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-amber-400 cursor-pointer overflow-hidden"
                onClick={() => toggleService(service.id)}
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-slate-900" size={32} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-900 transition-colors">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Expand Button */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-amber-600 font-semibold text-sm">
                      {isExpanded ? 'Show Less' : 'View Details'}
                    </span>
                    <ChevronDown
                      className={`text-amber-600 transition-transform duration-300 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                      size={20}
                    />
                  </div>

                  {/* Features List */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-gray-200 animate-in slide-in-from-top-2 duration-300">
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-900 to-slate-900 rounded-3xl p-8 lg:p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Need a Custom Solution?</h3>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Our team of experts is ready to develop tailored strategies and solutions for your unique challenges.
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-900 hover:from-amber-500 hover:to-yellow-700 font-semibold px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105"
          >
            Discuss Your Project
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
